// City-suggestion plumbing for the `suggest` auto-manage assist
// (play-modes spec §8): pull the sim's top ideas and apply one through
// the existing `start_building` intent (both build_structure and
// train_unit go down that path — the wasm infers `is_unit` from the id).
//
// Why a latest-ref for the fetcher: the SimWorld state object is replaced
// on every publish (tick rate), so `sim.suggestIntents` changes identity
// constantly. Reading it through a ref keeps the effect keyed on
// `enabled` alone instead of restarting the poll every tick.

import type { CitySuggestion } from '@aod/sim-adapter';
import { useCallback, useEffect, useRef, useState } from 'react';

/** The `start_building` arguments a suggestion maps to, or null when malformed. */
export function suggestionAction(idea: CitySuggestion): {
  cityId: string;
  buildingId: string;
} | null {
  if (idea.function_name !== 'build_structure' && idea.function_name !== 'train_unit') {
    return null;
  }
  const cityId = idea.args.city_id;
  const buildingId = idea.args.structure_id ?? idea.args.unit_kind;
  if (typeof cityId !== 'number' || !Number.isFinite(cityId)) return null;
  if (typeof buildingId !== 'string' || buildingId === '') return null;
  // Scene ids are `city_<entity>` — the same space as args.city_id
  // (entity.to_u64), which handle_start_building parses back.
  return { cityId: `city_${cityId}`, buildingId };
}

export interface CitySuggestions {
  readonly ideas: readonly CitySuggestion[];
  /** Re-pull immediately (after applying one, typically). */
  readonly refresh: () => void;
}

/**
 * Poll `getIdeas` while `enabled` (suggest mode + a city selected), and
 * clear when disabled. `intervalMs` defaults to 1s — suggestions depend
 * on treasury/queue state that changes every tick, so buttons must not
 * go stale for long. Errors pull an empty list (a missing/throwing wasm
 * method must never break the panel).
 */
export function useCitySuggestions(opts: {
  enabled: boolean;
  getIdeas: () => readonly CitySuggestion[];
  intervalMs?: number;
}): CitySuggestions {
  const { enabled, intervalMs = 1_000 } = opts;
  const getRef = useRef(opts.getIdeas);
  getRef.current = opts.getIdeas;
  const [ideas, setIdeas] = useState<readonly CitySuggestion[]>([]);

  const refresh = useCallback(() => {
    try {
      setIdeas(getRef.current());
    } catch {
      setIdeas([]);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIdeas([]);
      return;
    }
    refresh();
    const id = setInterval(refresh, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, refresh]);

  return { ideas, refresh };
}
